import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { Modal } from "../../components/ui-modal";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import Switch from "../../components/Switch";
import { useConfirm } from "../../components/ConfirmDialog";

const SAMPLE = { prenom: "Sarah", client: "Clinique Dental Care", nature: "Cabinet dentaire", projet: "Refonte du site", mission: "Montage vidéo — Reel", collaborateur: "Sofiane", montant: "1 500 €", date_echeance: "15/08/2026", phase: "Production", statut_phase: "Validée", numero_facture: "FAC-2026-014", email: "sarah@exemple.com" };
const render = (t, s) => Object.entries(s).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, v), t || "");

const emptyForm = { name: "", event: "", recipients: [], title: "", body: "", delay_value: 0, delay_unit: "hours" };

export default function AdminTaskAutomations() {
  const { confirm } = useConfirm();
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState({ events: [], recipient_roles: [], delay_units: [] });
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [catFilter, setCatFilter] = useState("all");

  const load = () => api.get("/admin/task-automations").then((r) => setItems(r.data));
  useEffect(() => {
    api.get("/admin/automation-events").then((r) => setCatalog(r.data));
    load();
  }, []);

  const eventByKey = useMemo(() => Object.fromEntries(catalog.events.map((e) => [e.key, e])), [catalog]);
  const roleLabel = useMemo(() => Object.fromEntries(catalog.recipient_roles.map((r) => [r.key, r.label])), [catalog]);
  const unitLabel = useMemo(() => Object.fromEntries((catalog.delay_units || []).map((u) => [u.key, u.label])), [catalog]);
  const currentEvent = eventByKey[form.event] || null;

  const categories = useMemo(() => {
    const counts = {};
    items.forEach((a) => { const c = a.event_category || "Autres"; counts[c] = (counts[c] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0], "fr"));
  }, [items]);
  const filteredItems = useMemo(
    () => (catFilter === "all" ? items : items.filter((a) => (a.event_category || "Autres") === catFilter)),
    [items, catFilter]
  );

  const delayText = (a) => {
    const ev = eventByKey[a.event];
    if (!ev) return "—";
    if (ev.mode === "scheduled") return `${a.delay_value || 0} ${unitLabel[a.delay_unit] || a.delay_unit || "j"} ${ev.schedule_direction === "before" ? "avant échéance" : "après échéance"}`;
    if (!a.delay_value) return "Immédiat";
    return `+${a.delay_value} ${unitLabel[a.delay_unit] || a.delay_unit}`;
  };

  const toggleActive = async (a) => { await api.put(`/admin/task-automations/${a.id}`, { active: !a.active }); toast.success(!a.active ? "Automatisation activée" : "Automatisation désactivée"); load(); };
  const openCreate = () => { const first = catalog.events[0]; setEditor({ mode: "create" }); setForm({ ...emptyForm, event: first?.key || "", recipients: first?.recipients || [] }); };
  const openEdit = (a) => { setEditor({ mode: "edit", id: a.id }); setForm({ name: a.name, event: a.event, recipients: a.recipients || [], title: a.title, body: a.body, delay_value: a.delay_value || 0, delay_unit: a.delay_unit || "hours" }); };

  const onEventChange = (key) => { const ev = eventByKey[key]; const allowed = ev?.recipients || []; setForm((f) => ({ ...f, event: key, recipients: (f.recipients || []).filter((r) => allowed.includes(r)) })); };
  const toggleRecipient = (r) => setForm((f) => ({ ...f, recipients: f.recipients.includes(r) ? f.recipients.filter((x) => x !== r) : [...f.recipients, r] }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Donnez un nom à l'automatisation");
    if (!form.event) return toast.error("Choisissez un déclencheur");
    if (!form.recipients.length) return toast.error("Choisissez au moins un destinataire");
    if (!form.title.trim()) return toast.error("Donnez un titre à la tâche");
    try {
      if (editor.mode === "create") { await api.post("/admin/task-automations", form); toast.success("Automatisation créée"); }
      else { await api.put(`/admin/task-automations/${editor.id}`, form); toast.success("Automatisation enregistrée"); }
      setEditor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'enregistrement"); }
  };
  const del = async (a) => { if (!(await confirm({ title: `Supprimer l'automatisation « ${a.name} » ?`, confirmLabel: "Supprimer", tone: "danger" }))) return; await api.delete(`/admin/task-automations/${a.id}`); toast.success("Automatisation supprimée"); load(); };

  const previewTitle = render(form.title, SAMPLE);
  const previewBody = render(form.body, SAMPLE);

  return (
    <div>
      <PageTitle overline="Automatisations" title="Tâches automatiques" />

      <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Icons.ListChecks size={16} className="text-marine" />
            <h2 className="text-sm font-semibold text-marine">Tâches automatiques</h2>
          </div>
          <p className="text-sm text-marine/80 mt-1 max-w-2xl">Génère automatiquement une notification-tâche (épinglée jusqu'à résolution) pour l'entreprise, l'agence ou le collaborateur assigné, lors d'un événement.</p>
        </div>
        <button onClick={openCreate} data-testid="create-task-automation" className="shrink-0 bg-marine text-white rounded-sm px-5 py-2.5 text-sm hover:bg-marine-hover transition-colors inline-flex items-center gap-2"><Icons.Plus size={16} /> Créer une automatisation</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="task-category-filters">
        <button onClick={() => setCatFilter("all")} data-testid="task-cat-filter-all"
          className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${catFilter === "all" ? "bg-bordeaux text-white border-bordeaux" : "bg-canvas text-marine border-marine/20 hover:border-marine/50"}`}>
          Toutes ({items.length})
        </button>
        {categories.map(([cat, count]) => (
          <button key={cat} onClick={() => setCatFilter(cat)} data-testid={`task-cat-filter-${cat}`}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${catFilter === cat ? "bg-bordeaux text-white border-bordeaux" : "bg-canvas text-marine border-marine/20 hover:border-marine/50"}`}>
            {cat} ({count})
          </button>
        ))}
      </div>

      <div className="bg-sable rounded-sm border border-marine/10 overflow-hidden" data-testid="task-automations-list">
        <div className="hidden md:grid grid-cols-[2fr_1.6fr_1.4fr_1.1fr_70px_32px] gap-4 px-5 py-3 text-xs uppercase tracking-wider text-marine/80 border-b border-marine/10 bg-sable">
          <span>Automatisation</span><span>Déclencheur</span><span>Destinataires</span><span>Délai</span><span>Statut</span><span></span>
        </div>
        {filteredItems.length === 0 && <p className="text-sm text-marine/80 p-5">Aucune automatisation de tâche dans cette catégorie.</p>}
        {filteredItems.map((a) => (
          <div key={a.id} data-testid={`task-automation-${a.id}`} className="grid md:grid-cols-[2fr_1.6fr_1.4fr_1.1fr_70px_32px] gap-4 px-5 py-4 border-b border-marine/5 items-center bg-creme hover:bg-sable">
            <button onClick={() => openEdit(a)} data-testid={`task-automation-edit-${a.id}`} className="text-left"><div className="font-medium text-marine flex items-center gap-1.5"><Icons.ListChecks size={16} className="text-bordeaux" /> {a.name}</div><div className="text-xs text-marine/80 truncate">{a.title}</div></button>
            <span className="text-sm text-marine">{a.event_label}</span>
            <span className="text-xs text-marine/80">{(a.recipients || []).map((r) => roleLabel[r] || r).join(", ")}</span>
            <span className="text-sm text-marine/80">{delayText(a)}</span>
            <Switch checked={!!a.active} onChange={() => toggleActive(a)} testid={`task-automation-toggle-${a.id}`}
              title={a.active ? "Désactiver cette automatisation" : "Activer cette automatisation"} className="justify-self-start md:justify-self-center" />
            <button onClick={() => del(a)} data-testid={`task-automation-delete-${a.id}`} className="justify-self-end text-rouge/70 hover:text-rouge transition-colors" title="Supprimer"><Icons.Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      {editor && (
        <Modal onClose={() => setEditor(null)} title={editor.mode === "create" ? "Nouvelle automatisation de tâche" : `Modifier — ${form.name}`} wide>
          <div className="grid lg:grid-cols-2 gap-6" data-testid="task-automation-editor">
            <div className="space-y-4">
              <label className="text-sm block">Nom de l'automatisation<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="task-edit-name" className="inp mt-1" placeholder="Ex : Relance validation d'étape" /></label>
              <label className="text-sm block">Déclencheur (événement)
                <select value={form.event} onChange={(e) => onEventChange(e.target.value)} data-testid="task-edit-event" className="inp mt-1">
                  {catalog.events.map((e) => <option key={e.key} value={e.key}>{e.category} — {e.label}</option>)}
                </select>
              </label>
              <div>
                <div className="text-sm mb-1.5">Destinataires de la tâche</div>
                <div className="flex flex-wrap gap-2" data-testid="task-edit-recipients">
                  {(currentEvent?.recipients || []).map((r) => (
                    <button key={r} type="button" onClick={() => toggleRecipient(r)} data-testid={`task-recipient-${r}`}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${form.recipients.includes(r) ? "bg-marine text-white border-marine" : "bg-white text-marine border-marine/25"}`}>
                      {form.recipients.includes(r) && <Icons.Check size={16} className="inline mr-1" />}{roleLabel[r] || r}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-sm mb-1.5">Délai de déclenchement</div>
                {currentEvent?.mode === "scheduled" ? (
                  <div className="flex items-center gap-2 text-sm" data-testid="task-edit-delay-scheduled">
                    <input type="number" min="0" value={form.delay_value} onChange={(e) => setForm({ ...form, delay_value: parseInt(e.target.value) || 0 })} data-testid="task-edit-delay-value" className="inp w-20" />
                    <select value={form.delay_unit} onChange={(e) => setForm({ ...form, delay_unit: e.target.value })} data-testid="task-edit-delay-unit" className="inp w-32">
                      {(catalog.delay_units || []).map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
                    </select>
                    <span className="text-marine/80">{currentEvent.schedule_direction === "before" ? "avant l'échéance" : "après l'échéance"}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm" data-testid="task-edit-delay-event">
                    <input type="number" min="0" value={form.delay_value} onChange={(e) => setForm({ ...form, delay_value: parseInt(e.target.value) || 0 })} data-testid="task-edit-delay-value" className="inp w-20" />
                    <select value={form.delay_unit} onChange={(e) => setForm({ ...form, delay_unit: e.target.value })} data-testid="task-edit-delay-unit" className="inp w-32">
                      {(catalog.delay_units || []).map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
                    </select>
                    <span className="text-marine/80">après l'événement (0 = immédiat)</span>
                  </div>
                )}
              </div>
              <label className="text-sm block">Titre de la tâche<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-edit-title" className="inp mt-1" placeholder="Ex : Valider l'étape {phase} du projet {projet}" /></label>
              <label className="text-sm block">Description<textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="task-edit-body" rows={5} className="inp mt-1 w-full resize-y" placeholder="Détail de la tâche à réaliser..." /></label>
              <div>
                <div className="text-xs uppercase tracking-wider text-bordeaux font-semibold mb-1.5">Variables disponibles</div>
                <div className="flex flex-wrap gap-1.5">
                  {(currentEvent?.variables || []).map((v) => <code key={v} className="text-[9px] px-2 py-1 rounded-sm bg-sable border border-marine/30 text-marine">{`{${v}}`}</code>)}
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="text-xs uppercase tracking-wider text-bordeaux font-semibold">Prévisualisation (données d'exemple)</div>
              <div className="rounded-sm border border-marine/10 overflow-hidden" data-testid="task-preview">
                <div className="bg-marine px-4 py-3 text-white text-sm font-medium flex items-center gap-2"><Icons.Bell size={16} /> {previewTitle || "Titre de la tâche"}</div>
                <div className="p-4 text-sm text-marine whitespace-pre-wrap">{previewBody || "Description de la tâche..."}</div>
              </div>
              <p className="text-xs text-marine/80">Cette tâche apparaîtra dans la cloche de notifications des destinataires et restera épinglée jusqu'à résolution.</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-marine/10">
            <button onClick={() => setEditor(null)} className="px-4 py-2 text-sm text-marine/80">Annuler</button>
            <button onClick={save} data-testid="task-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover">{editor.mode === "create" ? "Créer l'automatisation" : "Enregistrer"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
